import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { REALTIME_EVENTS } from './realtime.constants';
import type {
  SocketStatus,
  EventHandler,
  ChannelSubscription,
  RealtimeClient,
} from './realtime.types';

export function createRealtimeHooks(getClient: () => RealtimeClient) {
  function useSocket() {
    const [status, setStatus] = useState<SocketStatus>(() => getClient().getStatus());

    useEffect(() => {
      const client = getClient();
      const offConnected = client.on(REALTIME_EVENTS.CONNECTED, () => setStatus('connected'));
      const offDisconnected = client.on(REALTIME_EVENTS.DISCONNECTED, () =>
        setStatus('disconnected')
      );
      const offReconnecting = client.on(REALTIME_EVENTS.RECONNECTING, () =>
        setStatus('reconnecting')
      );
      const offError = client.on(REALTIME_EVENTS.ERROR, () => setStatus('error'));

      return () => {
        offConnected();
        offDisconnected();
        offReconnecting();
        offError();
      };
    }, []);

    const connect = useCallback(async () => {
      await getClient().connect();
    }, []);

    const disconnect = useCallback(() => {
      getClient().disconnect();
    }, []);

    return { status, isConnected: status === 'connected', connect, disconnect };
  }

  function useEvent<T = unknown>(event: string, handler: EventHandler<T>, enabled = true) {
    const handlerRef = useRef(handler);

    useEffect(() => {
      handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
      if (!enabled) return;
      return getClient().on(event, (data: T) => handlerRef.current(data));
    }, [event, enabled]);

    const emit = useCallback(
      (data?: unknown, ack?: (response: unknown) => void) => {
        getClient().emit(event, data, ack);
      },
      [event]
    );

    return { emit };
  }

  function useChannel<T = unknown>(
    channel: string,
    events: string[],
    handler: EventHandler<T>,
    enabled = true
  ): { isSubscribed: boolean; unsubscribe: () => void } {
    const [isSubscribed, setIsSubscribed] = useState(false);
    const subscriptionRef = useRef<ChannelSubscription<T> | null>(null);
    const handlerRef = useRef(handler);
    const eventsKey = useMemo(() => events.join(','), [events]);

    useEffect(() => {
      handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
      if (!enabled) {
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
          setIsSubscribed(false);
        }
        return;
      }

      const subscription = getClient().subscribe(channel, events, (data: T) => {
        handlerRef.current(data);
      });

      subscriptionRef.current = subscription;
      setIsSubscribed(true);

      return () => {
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
          setIsSubscribed(false);
        }
      };
      // `events` array identity changes each render; use stable `eventsKey` string in deps.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, eventsKey, enabled]);

    const unsubscribe = useCallback(() => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
        setIsSubscribed(false);
      }
    }, []);

    return { isSubscribed, unsubscribe };
  }

  function useBroadcast() {
    const [isConnected, setIsConnected] = useState(() => getClient().isConnected());

    useEffect(() => {
      const client = getClient();
      const offConnected = client.on(REALTIME_EVENTS.CONNECTED, () => setIsConnected(true));
      const offDisconnected = client.on(REALTIME_EVENTS.DISCONNECTED, () => setIsConnected(false));
      return () => {
        offConnected();
        offDisconnected();
      };
    }, []);

    const broadcast = useCallback(
      <T = unknown>(event: string, data: T, ack?: (response: unknown) => void) => {
        if (!isConnected) {
          console.warn('[Realtime] Cannot broadcast: socket not connected');
          return;
        }
        getClient().emit(event, data, ack);
      },
      [isConnected]
    );

    return { broadcast, isConnected };
  }

  return { useSocket, useEvent, useChannel, useBroadcast };
}
